import { useCallback, useEffect, useRef, useState } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { useIsRealInstance } from "./use-is-real-instance"
import { coreSlice, DEFAULT_CORE_STATE, CORE_PREFERENCE_FIELDS } from "../store/core-slice"
import { useDashboardPreferencesValidation } from "./preference-validation/use-dashboard-preferences-validation"

const PREFERENCE_SUBTYPE = "dashboard"
const PREFERENCE_FIELDS = CORE_PREFERENCE_FIELDS

/**
 * Syncs the core slice with user preferences via usePreferences.
 * Returns true once preferences have been loaded.
 */
export function useSyncCorePreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  // Only suppress this subtype's write when the workspace writer actually owns persistence here —
  // a flag-on account whose dashboard hasn't (or failed to) migrate to a real UUID instance has no
  // other writer, so it must keep using this legacy path (see use-is-real-instance.ts).
  const skipSave = useIsRealInstance(dashboardId)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const state = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)

  // Returning to a dashboard remounts this subtree (key={dashboardId}) and re-runs the one-shot
  // load. When the slice slot was already populated at mount it holds the live value — possibly an
  // edit the debounced save hadn't flushed — so skip the load rather than clobber it with the
  // older cached read.
  const [wasInitializedAtMount] = useState(() => state.isPreferencesLoaded)

  // Authoritative-at-mount (skipInitialLoad) means no validation pass runs; tell the override
  // effect so it arms from mount rather than waiting on that pass. [APP-3328]
  const setPreferenceValidationOptions = useDashboardPreferencesValidation(
    modelId,
    { isStateAuthoritativeAtMount: wasInitializedAtMount },
  )

  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncCorePreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime. "
        + "Unmount and remount DashboardLayout when navigating between dashboards, "
        + "or implement resetKey support in usePreferences.",
      )
    }
  }, [dashboardId])

  const setPreferencesAction = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (prefs: Record<string, any>) => coreSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  const isLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferencesAction,
    { setPreferenceValidationOptions, skipInitialLoad: wasInitializedAtMount, skipSave },
  )

  // When the user has no saved preferences, usePreferences sets isLoaded=true without
  // dispatching setPreferencesAction (because preferencesToApply is {}). This leaves
  // s.dashboard.core[dashboardId] undefined → isPreferencesLoaded stays false forever,
  // causing a permanent loading spinner for new users.
  useEffect(() => {
    if (isLoaded && !state.isPreferencesLoaded) {
      dispatch(coreSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  return state.isPreferencesLoaded
}
